import { z } from "zod";
import { CompaniesSchema, CompanySchema } from "../stages/stages.schemas";
import { NewFinancialStage } from "../shared";

export const InnoveraCompanySchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  website: z.string().nullable(),
  logoUrl: z.string().nullable(),
  headline: z.string().nullable(),
  description: z.string().nullable(),
  financialStage: z.nativeEnum(NewFinancialStage).nullable(),
  totalFunding: z.coerce.bigint().nullable(),
  foundingDate: z.coerce.date().nullable(),
  country: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  angelListUrl: z.string().nullable(),
  email: z.string().default(""),
  phone: z.string().default(""),
  facebookUrl: z.string().nullable(),
  githubUrl: z.string().nullable(),
  intstagramUrl: z.string().nullable(),
  linkedInUrl: z.string().nullable(),
  productHuntUrl: z.string().nullable(),
  twitterUrl: z.string().nullable(),
  harmonicInvestors: z.array(
    z.object({
      name: z.string(),
      type: z.enum(["organization", "individual"]),
      imageUrl: z.string().nullable()
    })
  ),
  harmonicTags: z.array(z.string()).default([]),

  headcount: z.coerce.number().nullable(),
  lastFundingAmount: z.coerce.bigint().nullable(),
  lastFundingDate: z.coerce.date().nullable(),
  fundingRoundsCount: z.coerce.number().nullable()
});

export type TInnoveraCompany = z.infer<typeof InnoveraCompanySchema>;

const StageSchema = z.object({
  id: z.number(),
  name: z.string(),
  pipeline: z.object({
    id: z.number(),
    name: z.string()
  })
});
const TagSchema = z.object({
  id: z.number(),
  name: z.string()
});

export const ParsingStatusSchema = z.enum(["processing", "done", "matching_done", "error"]);

export type TParsingStatus = z.infer<typeof ParsingStatusSchema>;

export enum EDataStatus {
  processing = "processing",
  done = "done",
  matching_done = "matching_done",
  error = "error"
}

// company details

export const GetPortfolioCompanySchema = z.object({
  parsingStatus: ParsingStatusSchema,
  parsingMessage: z.string().nullable(),
  parsingProgress: z.number().nullable(),
  hasPitchdeckData: z.boolean().optional(),
  hasInnoveraData: z.boolean().optional(),
  id: z.number(),
  name: z.string().nullable(),
  website: z.string().nullable(),
  description: z.string().nullable(),
  headline: z.string().nullable(),
  tam: z.coerce.number().nullable(),
  sam: z.coerce.number().nullable(),
  som: z.coerce.number().nullable(),
  country: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  industries: z.string().nullable(),
  businessModels: z.string().nullable(),
  companyData: InnoveraCompanySchema,
  tags: z.array(TagSchema),
  teamMembers: z
    .array(
      z.object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string().nullable(),
        title: z.string().nullable(),
        linkedInUrl: z.string().nullable(),
        rank: z.number(),
        profilePhotoUrl: z.string().nullish()
      })
    )
    .default([]),
  stage: StageSchema,
  parsedCompetitors: CompaniesSchema.default([]),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  solutionDescription: z.string().nullable(),
  marketDescription: z.string().nullable(),
  twitterUrl: z.string().nullable().default(null),
  linkedInUrl: z.string().nullable().default(null),
  facebookUrl: z.string().nullable().default(null),
  logoUrl: z.string().nullable().default(null)
});

export type TGetPortfolioCompany = z.infer<typeof GetPortfolioCompanySchema>;

export const GetPortfolioCompaniesSchema = z.array(GetPortfolioCompanySchema);

// competitors

export const CompetitorsSchema = z.array(CompanySchema);
export type TCompetitor = z.infer<typeof CompanySchema>;
export type TCompetitorsSchema = z.infer<typeof CompetitorsSchema>;
